import { useLocalStorageValue } from '@react-hookz/web';
import { isNullOrUndefined } from 'helpers/ts-helpers';
import { useInterval } from 'hooks/useInterval';
import intersection from 'lodash/intersection';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useState } from 'react';
import { HIDDEN_WEB_INCIDENTS } from 'services/localStorer';
import { useAutoFetchActiveWebIncidents } from 'services/networking/KickStuckApiHooks';
import { WebIncident } from 'types/webIncident';

const WEB_INCIDENTS_FETCH_INTERVAL = 60 * 1000 * 5; // 5 minutes in seconds;

export const useActiveWebIncidents = () => {
  const { responseBody, doFetch } = useAutoFetchActiveWebIncidents();
  const [activeWebIncidents, setActiveWebIncidents] = useState<WebIncident[]>([]);
  const [hiddenWebIncidents, setHiddenWebIncidents] = useLocalStorageValue<string[]>(
    HIDDEN_WEB_INCIDENTS,
    [],
  );

  useEffect(() => {
    if (isNullOrUndefined(responseBody)) {
      return;
    }

    const visibleIncidents = responseBody.filter(
      incident => !hiddenWebIncidents.includes(`${incident.id}-${incident.updateDate}`),
    );
    if (!isEqual(visibleIncidents, activeWebIncidents)) {
      setActiveWebIncidents(visibleIncidents);
      setHiddenWebIncidents(
        intersection(
          hiddenWebIncidents,
          responseBody.map(({ id, updateDate }) => `${id}-${updateDate}`),
        ),
      );
    }
  }, [
    responseBody,
    hiddenWebIncidents,
    activeWebIncidents,
    setActiveWebIncidents,
    setHiddenWebIncidents,
  ]);

  const hideWebIncident = useCallback(
    (incident: WebIncident) => {
      setHiddenWebIncidents(alreadyHiddenIncidents =>
        alreadyHiddenIncidents.concat(`${incident.id}-${incident.updateDate}`),
      );
    },
    [setHiddenWebIncidents],
  );

  useInterval(doFetch, WEB_INCIDENTS_FETCH_INTERVAL);

  return { activeWebIncidents, hideWebIncident };
};
